import React, { useState } from "react";
import {
  Row,
  Col,
  CardBody,
  Card,
  Container,
  Form,
  Input,
  FormFeedback,
  Button,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { useRecoilState } from "recoil";
import authState from "../atom/auth";
import { login } from "../api/auth";
import ResultHandler from "../components/ResultHandler";
import logo from "../assets/images/redcrm.png";
import useCustomValidation from "./dashboard/validationSchema";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import "./Login.css";

const Login = () => {
  const { t } = useTranslation();
  const { loginSchema } = useCustomValidation();
  const [isLogged, setIsLogged] = useRecoilState(authState);
  const history = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: localStorage.getItem("email") || "",
      password: localStorage.getItem("password") || "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await login({
          email: values.email,
          password: values.password,
        });

        if (data.success === false) {
          throw new Error(data.message || "Login failed");
        }

        const admin = data?.data?.admin;
        const role = admin?.role;
        if (!role || !role?.name) {
          throw new Error("No role assigned to your account.");
        }
        const permissions = role?.permissions;
        if (!permissions || permissions.length === 0) {
          throw new Error("No permissions assigned to your role.");
        }

        const canAccessSales = permissions.some((p) => p.resource === "Sales");
        const canAccessMonitor = permissions.some(
          (p) => p.resource === "Monitor"
        );
        const canAccessManagement = permissions.some(
          (p) => p.resource === "Management"
        );

        const roleName = role.name;

        localStorage.setItem("token", data?.data?.token);
        localStorage.setItem("admin", JSON.stringify(admin));
        localStorage.setItem("currentUser", JSON.stringify(admin));
        localStorage.setItem("role", roleName);
        localStorage.setItem("permissions", JSON.stringify(permissions));

        if (rememberMe) {
          localStorage.setItem("email", values.email);
          localStorage.setItem("password", values.password);
        } else {
          localStorage.removeItem("email");
          localStorage.removeItem("password");
        }

        setIsLogged(true);
        setLoading(false);

        if (roleName !== "super admin" && canAccessSales && canAccessMonitor) {
          history("/deals");
        } else if (roleName !== "super admin" && canAccessMonitor) {
          history("/leads");
        } else if (roleName !== "super admin" && canAccessSales) {
          history("/deals");
        } else if (roleName !== "super admin" && canAccessManagement) {
          history("/reports");
        } else {
          history("/admins");
        }
      } catch (err) {
        console.error("Login Error:", err);
        const message =
          err?.response?.data?.message || err.message || "Something went wrong";
        setError(message);
        setLoading(false);
      }
    },
  });

  return (
    <div className="auth-layout">
      <Container className="h-100">
        <Row className="justify-content-center align-items-center h-100">
          <Col md={8} lg={6} xl={5}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="login-box shadow-lg">
                <CardBody className="p-4 p-md-5">
                  <motion.div
                    className="text-center mb-4"
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <img src={logo} alt="Logo" className="logo-animation" />
                    <h2 className="mt-3 mb-1">{t("Welcome Back")}</h2>
                    <p className="text-muted">{t("Sign in to continue")}</p>
                  </motion.div>

                  <Form
                    className="form-horizontal"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                    >
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">
                          {t("Email Address")}
                        </label>
                        <InputGroup className="mb-3">
                          <InputGroupText className="input-group-text">
                            <i className="bx bx-envelope"></i>
                          </InputGroupText>
                          <Input
                            id="email"
                            name="email"
                            placeholder={t("Enter Email")}
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email}
                            invalid={
                              !!(
                                validation.touched.email &&
                                validation.errors.email
                              )
                            }
                            className="form-control-lg"
                          />
                        </InputGroup>
                        {validation.touched.email &&
                          validation.errors.email && (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          )}
                      </div>

                      <div className="mb-3">
                        <label htmlFor="password" className="form-label">
                          {t("Password")}
                        </label>
                        <InputGroup className="mb-3">
                          <InputGroupText className="input-group-text">
                            <i className="bx bx-lock-alt"></i>
                          </InputGroupText>
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            placeholder={t("Enter Password")}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password}
                            invalid={
                              !!(
                                validation.touched.password &&
                                validation.errors.password
                              )
                            }
                            className="form-control-lg"
                          />
                        </InputGroup>
                        {validation.touched.password &&
                          validation.errors.password && (
                            <FormFeedback type="invalid">
                              {validation.errors.password}
                            </FormFeedback>
                          )}
                      </div>

                      {/* <div className="mb-3 d-flex justify-content-between align-items-center">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="rememberMe"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="rememberMe"
                          >
                            {t("Remember Me")}
                          </label>
                        </div>
                        <Link to="/forgot-password" className="text-primary">
                          {t("Forgot Password?")}
                        </Link>
                      </div> */}

                      <ResultHandler error={error} />

                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        className="mb-3"
                      >
                        <div className="d-grid">
                          <ResultHandler error={error} />
                          <Button
                            color="primary"
                            size="lg"
                            type="submit"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <i className="bx bx-hourglass bx-spin font-size-16 align-middle me-2"></i>
                                {t("loading")}
                              </>
                            ) : (
                              t("Login")
                            )}
                          </Button>
                        </div>
                      </motion.div>
                    </motion.div>
                  </Form>

                  <motion.div
                    className="text-center mt-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    {/* <p className="text-muted mb-0">
                      {t("Don't have an account?")}{" "}
                      <Link to="/register" className="text-primary fw-semibold">
                        {t("Sign Up")}
                      </Link>
                    </p> */}
                  </motion.div>
                </CardBody>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Login;
